import type {
  AcademicTerm,
  AssignmentCopyRequest,
  AssignmentFolderAction,
  AttendanceRecord,
  CalendarEvent,
  Classroom,
  DailyMemoAction,
  DailyRecord,
  DailyRecordAction,
  DrawAction,
  HomeroomAffairChange,
  HomeroomHomeworkRecord,
  HomeroomRecord,
  HomeworkAssignment,
  HomeworkRecord,
  SeatingChart,
  Student,
  StudentRoutineItem,
  Workspace,
} from "@classroom/domain";
import { academicYearTerms, hongKongDate, schoolYearStart } from "@classroom/domain";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ClassSetupDraft } from "../classSetup";
import type { WorkspaceData } from "../data";
import { syncReviewId } from "../driveSync/reviewScope";
import type { HistoryPlan } from "../recordImport";
import { LEGACY_WORKSPACE_ID, type LocalAccount } from "./accountScope";
import { getLocalStorageStatus, LocalStoreClient } from "./client";
import type { LocalSnapshot, StoredRecord } from "./repository";
import type { Collection } from "./schema";

export const LOCAL_WORKSPACE_ID = LEGACY_WORKSPACE_ID;

export function newWorkspace(workspaceId: string, ownerUid = "local-device"): Workspace {
  const now = new Date().toISOString();
  const start = schoolYearStart(hongKongDate());
  const year = {
    id: `school-year-${start}`,
    name: `${start}–${start + 1}`,
    startDate: `${start}-09-01`,
    endDate: `${start + 1}-08-31`,
    status: "open" as const,
  };
  return {
    id: workspaceId,
    name: "我的本地課堂",
    ownerUid,
    accessMode: "trial",
    trialEndsAt: new Date(Date.now() + 7 * 86_400_000).toISOString(),
    retentionDays: 0,
    createdAt: now,
    updatedAt: now,
    schoolYears: [year],
    currentSchoolYearId: year.id,
    academicTerms: academicYearTerms(start, 2),
  };
}

function emptyData(): WorkspaceData {
  return {
    hiddenTools: [],
    classes: [],
    students: [],
    dailyRecords: [],
    homeworkAssignments: [],
    homeworkRecords: [],
    calendarEvents: [],
    homeroomRecords: [],
    attendanceRecords: [],
    homeroomHomeworkRecords: [],
    studentRoutineItems: [],
    seatingCharts: [],
    quizzes: [],
    quizSessions: [],
    quizResponses: [],
    loading: true,
  };
}

function snapshotToData(snapshot: LocalSnapshot): WorkspaceData {
  const result = {
    ...emptyData(),
    workspace: snapshot.workspace as unknown as Workspace,
    hiddenTools: snapshot.hiddenTools,
    loading: false,
  };
  for (const row of snapshot.records) {
    switch (row.collection) {
      case "classes":
        result.classes.push(row.data as unknown as Classroom);
        break;
      case "students":
        result.students.push(row.data as unknown as Student);
        break;
      case "dailyRecords":
        result.dailyRecords.push(row.data as unknown as DailyRecord);
        break;
      case "homeworkAssignments":
        result.homeworkAssignments.push(row.data as unknown as HomeworkAssignment);
        break;
      case "homeworkRecords":
        result.homeworkRecords.push(row.data as unknown as HomeworkRecord);
        break;
      case "calendarEvents":
        result.calendarEvents.push(row.data as unknown as CalendarEvent);
        break;
      case "homeroomRecords":
        result.homeroomRecords.push(row.data as unknown as HomeroomRecord);
        break;
      case "attendanceRecords":
        result.attendanceRecords.push(row.data as unknown as AttendanceRecord);
        break;
      case "homeroomHomeworkRecords":
        result.homeroomHomeworkRecords.push(row.data as unknown as HomeroomHomeworkRecord);
        break;
      case "studentRoutineItems":
        result.studentRoutineItems.push(row.data as unknown as StudentRoutineItem);
        break;
      case "seatingCharts":
        result.seatingCharts.push(row.data as unknown as SeatingChart);
        break;
    }
  }
  return result;
}

function asStored<T extends { id: string; workspaceId?: string }>(record: T): StoredRecord {
  return record as unknown as StoredRecord;
}

export function useLocalWorkspace(enabled: boolean, assertWritable?: () => void, account?: LocalAccount) {
  const workspaceId = account?.workspaceId || LOCAL_WORKSPACE_ID;
  const storageKey = (key: string) => (account ? `${key}:${workspaceId}` : key);
  const writeGuardRef = useRef(assertWritable);
  writeGuardRef.current = assertWritable;
  const [data, setData] = useState<WorkspaceData>(emptyData);
  const [storageStatus, setStorageStatus] = useState<Awaited<ReturnType<typeof getLocalStorageStatus>>>();
  const clientRef = useRef<LocalStoreClient | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const selectedYearRef = useRef<string | undefined>(
    sessionStorage.getItem(storageKey("classroom-browsing-school-year-date")) === hongKongDate()
      ? sessionStorage.getItem(storageKey("classroom-browsing-school-year")) || undefined
      : undefined,
  );
  const [selectedYearId, setSelectedYearId] = useState<string | undefined>(selectedYearRef.current);
  const refreshVersion = useRef(0);

  const refresh = useCallback(async () => {
    const client = clientRef.current;
    if (!client) throw new Error("本地資料庫尚未開啟。");
    const version = ++refreshVersion.current;
    const date = hongKongDate();
    let writable = true;
    try {
      writeGuardRef.current?.();
    } catch {
      writable = false;
    }
    if (writable) await client.ensureDateSchoolYear(workspaceId, date);
    let snapshot: LocalSnapshot;
    try {
      snapshot = await client.loadYear(workspaceId, selectedYearRef.current, date);
    } catch (error) {
      if (!(error instanceof Error) || error.message !== "unknown-year") throw error;
      sessionStorage.removeItem(storageKey("classroom-browsing-school-year"));
      sessionStorage.removeItem(storageKey("classroom-browsing-school-year-date"));
      snapshot = await client.loadYear(workspaceId, undefined, date);
    }
    if (clientRef.current === client && version === refreshVersion.current) {
      selectedYearRef.current = snapshot.selectedSchoolYearId;
      setSelectedYearId(snapshot.selectedSchoolYearId);
      setData(snapshotToData(snapshot));
    }
  }, [workspaceId]);

  const selectYear = useCallback(
    async (id?: string) => {
      await queueRef.current;
      const previous = selectedYearRef.current;
      selectedYearRef.current = id;
      try {
        await refresh();
        if (id) {
          sessionStorage.setItem(storageKey("classroom-browsing-school-year"), id);
          sessionStorage.setItem(storageKey("classroom-browsing-school-year-date"), hongKongDate());
        } else {
          sessionStorage.removeItem(storageKey("classroom-browsing-school-year"));
          sessionStorage.removeItem(storageKey("classroom-browsing-school-year-date"));
        }
      } catch (error) {
        selectedYearRef.current = previous;
        throw error;
      }
    },
    [refresh],
  );

  useEffect(() => {
    if (!enabled) return;
    const client = new LocalStoreClient(account);
    const channel = new BroadcastChannel(`classroom-local-workspace-changes:${workspaceId}`);
    clientRef.current = client;
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent<{ workspaceId?: string }>) => {
      if (event.data?.workspaceId === workspaceId) void refresh().catch(() => undefined);
    };
    let active = true;
    void getLocalStorageStatus()
      .then((status) => {
        if (active) setStorageStatus(status);
      })
      .catch(() => undefined);
    void client
      .ensureWorkspace(asStored(newWorkspace(workspaceId, account?.userId)))
      .then(() => refresh())
      .catch((error: unknown) => {
        if (active)
          setData((current) => ({
            ...current,
            loading: false,
            error: error instanceof Error ? error.message : String(error),
          }));
      });
    return () => {
      active = false;
      if (clientRef.current && clientRef.current !== client) clientRef.current.close();
      clientRef.current = null;
      channelRef.current = null;
      channel.close();
      client.close();
    };
  }, [enabled, refresh, workspaceId]);

  const mutate = useCallback(
    <T>(operation: (client: LocalStoreClient) => Promise<T>): Promise<T> => {
      const job = queueRef.current.then(async () => {
        // Check at execution time, including work queued before grace expired.
        writeGuardRef.current?.();
        const client = clientRef.current;
        if (!client) throw new Error("本地資料庫尚未開啟。");
        const result = await operation(client);
        await refresh();
        channelRef.current?.postMessage({ workspaceId: workspaceId });
        return result;
      });
      queueRef.current = job.catch(() => undefined);
      return job;
    },
    [refresh],
  );

  const actions = useMemo(() => {
    const stamp = <T extends { id: string; workspaceId?: string }>(collection: Collection, item: T): StoredRecord =>
      collection === "classes" || collection === "calendarEvents"
        ? { ...asStored(item), schoolYearId: asStored(item).schoolYearId ?? selectedYearRef.current }
        : asStored(item);
    const save = <T extends { id: string; workspaceId?: string }>(collection: Collection, item: T) => {
      const record = stamp(collection, item);
      return mutate((client) => client.put(workspaceId, collection, record));
    };
    const saveMany = <T extends { id: string; workspaceId?: string }>(collection: Collection, items: T[]) => {
      const records = items.map((item) => stamp(collection, item));
      return mutate((client) => client.putMany(workspaceId, collection, records));
    };
    const remove = (collection: Collection, id: string) =>
      mutate((client) => client.remove(workspaceId, collection, id));
    const removeMany = (collection: Collection, ids: string[]) =>
      mutate((client) => client.removeMany(workspaceId, collection, ids));
    const patch = async <T extends { id: string; workspaceId?: string }>(
      collection: Collection,
      id: string,
      update: Partial<T>,
    ) =>
      mutate(async (client) => {
        const current = await client.get(workspaceId, collection, id);
        if (!current) throw new Error("找不到要更新的紀錄。");
        await client.put(workspaceId, collection, { ...current, ...update, id, updatedAt: new Date().toISOString() });
      });

    return {
      recoverConnection: async () => {
        await queueRef.current;
        if (!clientRef.current?.hasFailed()) return;
        clientRef.current.close();
        const replacement = new LocalStoreClient(account);
        clientRef.current = replacement;
        // Explicit retry opens a connection without replaying any uncertain write.
        await replacement.ensureWorkspace(asStored(newWorkspace(workspaceId, account?.userId)));
        await refresh();
      },
      saveClassSetup(draft: ClassSetupDraft) {
        const yearId = selectedYearRef.current;
        return mutate((client) => client.saveClassSetup(workspaceId, draft, yearId));
      },
      async addClass(name: string, classRole: Classroom["classRole"], sharedDataClassId?: string) {
        const now = new Date().toISOString();
        await save("classes", {
          id: `class_${crypto.randomUUID()}`,
          workspaceId,
          name,
          classRole,
          sharedDataClassId,
          active: true,
          createdAt: now,
          updatedAt: now,
        } satisfies Classroom);
      },
      addStudent(classId: string, row: import("@classroom/domain").RosterRow) {
        return mutate((client) => client.addStudent(workspaceId, classId, row));
      },
      importStudents(classId: string, rows: import("@classroom/domain").RosterRow[]) {
        return mutate((client) => client.importStudents(workspaceId, classId, rows));
      },
      updateStudent: (studentId: string, update: Partial<Student>) => patch<Student>("students", studentId, update),
      deleteEmptyClass: (classId: string, confirmedName: string) =>
        mutate((client) => client.deleteEmptyClass(workspaceId, classId, confirmedName)),
      deleteStudentData: (studentId: string) => mutate((client) => client.deleteStudentData(workspaceId, studentId)),
      deleteTeachingAssignment: (classId: string, assignmentId: string, confirmationToken: string) =>
        mutate((client) => client.deleteTeachingAssignment(workspaceId, classId, assignmentId, confirmationToken)),
      updateClassSettings: (
        classId: string,
        update: Partial<Pick<Classroom, "customSubjects" | "customDuties" | "dutyAssignments">>,
      ) => mutate((client) => client.updateClassSettings(workspaceId, classId, update)),
      saveDrawAction: (classId: string, action: DrawAction) =>
        mutate((client) => client.saveDrawAction(workspaceId, classId, action)),
      saveAssignmentFolderAction: (classId: string, action: AssignmentFolderAction) =>
        mutate((client) => client.saveAssignmentFolderAction(workspaceId, classId, action)),
      saveHomeroomAffairChange: (classId: string, date: string, change: HomeroomAffairChange) =>
        mutate((client) => client.saveHomeroomAffairChange(workspaceId, classId, date, change)),
      copyTeachingAssignments: (request: AssignmentCopyRequest) =>
        mutate((client) => client.copyTeachingAssignments(workspaceId, request)),
      saveDailyMemoAction: (classId: string, action: DailyMemoAction) =>
        mutate((client) => client.saveDailyMemoAction(workspaceId, classId, action)),
      saveAcademicTerms: (terms: AcademicTerm[]) => {
        const yearId = selectedYearRef.current;
        return mutate((client) => client.saveAcademicTerms(workspaceId, terms, yearId));
      },
      saveSchoolYearAction: (action: import("@classroom/domain").SchoolYearAction) =>
        mutate((client) => client.saveSchoolYearAction(workspaceId, action)),
      previewSchoolYearDeletion: async (id: string) => {
        await queueRef.current;
        const client = clientRef.current;
        if (!client) throw new Error("本地資料庫尚未開啟。");
        return client.previewSchoolYearDeletion(workspaceId, id);
      },
      saveHiddenTools: (hiddenTools: string[]) => mutate((client) => client.saveHiddenTools(workspaceId, hiddenTools)),
      saveDailyRecord: (item: DailyRecord) => save("dailyRecords", item),
      saveDailyRecordAction: (classId: string, date: string, action: DailyRecordAction) =>
        mutate((client) => client.saveDailyRecordAction(workspaceId, classId, date, action)),
      saveHomeworkAssignment: (item: HomeworkAssignment) => save("homeworkAssignments", item),
      saveHomeworkRecord: (item: HomeworkRecord) => save("homeworkRecords", item),
      saveTeachingRecords: (
        events: CalendarEvent[],
        assignments: HomeworkAssignment[],
        records: HomeworkRecord[] = [],
      ) => {
        const stampedEvents = events.map((item) => stamp("calendarEvents", item));
        return mutate((client) =>
          client.putCollections(workspaceId, [
            { collection: "calendarEvents", records: stampedEvents },
            { collection: "homeworkAssignments", records: assignments.map(asStored) },
            { collection: "homeworkRecords", records: records.map(asStored) },
          ]),
        );
      },
      saveCalendarEvent: (item: CalendarEvent) => save("calendarEvents", item),
      saveCalendarEvents: (items: CalendarEvent[]) => saveMany("calendarEvents", items),
      saveSchoolCalendarChanges: (changes: import("@classroom/domain").SchoolCalendarChange[]) =>
        mutate((client) => client.saveSchoolCalendarChanges(workspaceId, changes)),
      deleteCalendarEvent: (id: string) => remove("calendarEvents", id),
      deleteCalendarEvents: (ids: string[]) => removeMany("calendarEvents", ids),
      saveHomeroomRecord: (item: HomeroomRecord) => save("homeroomRecords", item),
      saveAttendanceRecord: (item: AttendanceRecord) => save("attendanceRecords", item),
      saveHomeroomHomeworkRecord: (item: HomeroomHomeworkRecord) => save("homeroomHomeworkRecords", item),
      deleteHomeroomHomeworkRecord: (id: string) => remove("homeroomHomeworkRecords", id),
      saveStudentRoutineItem: (item: StudentRoutineItem) => save("studentRoutineItems", item),
      deleteStudentRoutineItem: (id: string) => remove("studentRoutineItems", id),
      saveHistoryPlan: (plan: HistoryPlan) =>
        mutate((client) =>
          client.putCollections(workspaceId, [
            { collection: "attendanceRecords", records: plan.attendanceRecords.map(asStored) },
            { collection: "homeroomHomeworkRecords", records: plan.homeroomHomeworkRecords.map(asStored) },
            { collection: "dailyRecords", records: plan.dailyRecords.map(asStored) },
            { collection: "studentRoutineItems", records: plan.studentRoutineItems.map(asStored) },
          ]),
        ),
      saveSeatingChart: (item: SeatingChart) => save("seatingCharts", item),
      previewCloudImport: async (text: string) => {
        await queueRef.current;
        const client = clientRef.current;
        if (!client) throw new Error("本地資料庫尚未開啟。");
        return client.previewCloudImport(workspaceId, text);
      },
      importCloudExport: (text: string, checksum: string, targetChecksum: string) =>
        mutate((client) => client.importCloudExport(workspaceId, text, checksum, targetChecksum)),
      exportSnapshot: async () => {
        const client = clientRef.current;
        if (!client) throw new Error("本地資料庫尚未開啟。");
        await queueRef.current;
        return client.exportBackupSource(workspaceId);
      },
      restoreSnapshot: (snapshot: LocalSnapshot, sqliteImage?: string, schemaVersion?: number) =>
        mutate(async (client) => {
          if (sqliteImage) {
            if (typeof schemaVersion !== "number") throw new Error("備份缺少資料庫版本。");
            await client.validateBackupImage(workspaceId, snapshot, sqliteImage, schemaVersion);
          }
          if (
            snapshot.records.some(
              (row) => row.data.workspaceId !== undefined && row.data.workspaceId !== snapshot.workspace.id,
            )
          )
            throw new Error("備份檔的工作區不符。");
          const restored: LocalSnapshot = {
            ...snapshot,
            workspace: { ...snapshot.workspace, id: workspaceId, ownerUid: account?.userId || "local-device" },
            records: snapshot.records.map((row) => ({ ...row, data: { ...row.data, workspaceId } })),
          };
          await client.validateSnapshot(workspaceId, restored);
          // The real replacement checks integrity again in its own transaction.
          writeGuardRef.current?.();
          await client.replaceSnapshot(workspaceId, restored);
        }),
    };
  }, [mutate, workspaceId, account]);

  return {
    data,
    actions,
    storageStatus,
    updateStorageStatus: setStorageStatus,
    selectedYearId,
    selectYear,
  };
}
